import { useEffect, useRef, useState } from 'react';
import { Mic, Square, Loader2, Trash2, Upload, Info } from 'lucide-react';

interface AudioRecorderProps {
    onAudioReady: (audioBase64: string, mimeType: string, durationSec: number) => void;
    onReset?: () => void;
    disabled?: boolean;
    isUploading?: boolean;
}

const MAX_BYTES = 24 * 1024 * 1024; // Whisper API limit is 25 MB — leave headroom

function pickMimeType(): string {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
    for (const c of candidates) {
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) return c;
    }
    return 'audio/webm';
}

async function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.includes(',') ? result.split(',')[1] : result;
            resolve(base64);
        };
        reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
        reader.readAsDataURL(blob);
    });
}

function formatSeconds(total: number): string {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function probeAudioDuration(blobUrl: string): Promise<number> {
    return new Promise(resolve => {
        const el = document.createElement('audio');
        el.src = blobUrl;
        el.preload = 'metadata';
        const done = (n: number) => {
            el.removeEventListener('loadedmetadata', onMeta);
            el.removeEventListener('error', onErr);
            resolve(n);
        };
        const onMeta = () => {
            const d = el.duration;
            done(isFinite(d) && d > 0 ? Math.round(d) : 0);
        };
        const onErr = () => done(0);
        el.addEventListener('loadedmetadata', onMeta);
        el.addEventListener('error', onErr);
        // Fail-safe — probe should never stall the UX
        setTimeout(() => done(0), 3000);
    });
}

export function AudioRecorder({ onAudioReady, onReset, disabled, isUploading }: AudioRecorderProps) {
    const [state, setState] = useState<'idle' | 'requesting' | 'recording' | 'preview' | 'error'>('idle');
    const [source, setSource] = useState<'recording' | 'upload' | null>(null);
    const [elapsed, setElapsed] = useState(0);
    const [previewLabel, setPreviewLabel] = useState<string>('');
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [chosenMime, setChosenMime] = useState<string | null>(null);
    const [showIphoneHint, setShowIphoneHint] = useState(false);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const streamRef = useRef<MediaStream | null>(null);
    const timerRef = useRef<number | null>(null);
    const startTsRef = useRef<number>(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) window.clearInterval(timerRef.current);
            if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const startRecording = async () => {
        setErrorMsg(null);
        setState('requesting');
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mimeType = pickMimeType();
            setChosenMime(mimeType);
            const mr = new MediaRecorder(stream, { mimeType });
            chunksRef.current = [];
            mr.ondataavailable = e => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
            mr.onstop = handleRecordingStop;
            mr.start(1000);
            mediaRecorderRef.current = mr;
            startTsRef.current = Date.now();
            setElapsed(0);
            setSource('recording');
            setState('recording');
            timerRef.current = window.setInterval(() => {
                setElapsed(Math.floor((Date.now() - startTsRef.current) / 1000));
            }, 500);
        } catch (err: any) {
            setErrorMsg(err?.message || 'Microphone access was denied. Allow microphone access and try again.');
            setState('error');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
        if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    };

    const handleRecordingStop = async () => {
        const mimeType = chosenMime || pickMimeType();
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size < 1024) {
            setErrorMsg('Recording is too short. Try again and speak for at least a few seconds.');
            setState('error');
            return;
        }
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setPreviewLabel(`Recorded · ${formatSeconds(elapsed)} · ${formatBytes(blob.size)}`);
        setState('preview');
        try {
            const base64 = await blobToBase64(blob);
            onAudioReady(base64, mimeType, elapsed);
        } catch (err: any) {
            setErrorMsg(err?.message || 'Failed to encode audio.');
            setState('error');
        }
    };

    const handleFileChosen = async (file: File | null) => {
        if (!file) return;
        setErrorMsg(null);
        if (!file.type.startsWith('audio/') && !/\.(m4a|mp3|mp4|wav|ogg|webm|mpga|mpeg)$/i.test(file.name)) {
            setErrorMsg('Please pick an audio file (m4a, mp3, wav, ogg, webm, mp4).');
            setState('error');
            return;
        }
        if (file.size > MAX_BYTES) {
            setErrorMsg(`File is ${formatBytes(file.size)} — maximum allowed is ${formatBytes(MAX_BYTES)}. Split the recording or re-export at a lower bitrate.`);
            setState('error');
            return;
        }
        if (file.size < 1024) {
            setErrorMsg('File is too small. Please pick a longer recording.');
            setState('error');
            return;
        }

        // Infer mimeType. Some iOS exports report empty type; fall back on file extension.
        let mimeType = file.type;
        if (!mimeType) {
            if (/\.m4a$/i.test(file.name)) mimeType = 'audio/mp4';
            else if (/\.mp3$/i.test(file.name)) mimeType = 'audio/mpeg';
            else if (/\.wav$/i.test(file.name)) mimeType = 'audio/wav';
            else if (/\.ogg$/i.test(file.name)) mimeType = 'audio/ogg';
            else if (/\.webm$/i.test(file.name)) mimeType = 'audio/webm';
            else mimeType = 'audio/mp4';
        }

        const blob = new Blob([await file.arrayBuffer()], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const durationSec = await probeAudioDuration(url);

        setPreviewUrl(url);
        setSource('upload');
        setElapsed(durationSec);
        setPreviewLabel(
            `${file.name} · ${formatBytes(file.size)}${durationSec ? ` · ${formatSeconds(durationSec)}` : ''}`
        );
        setState('preview');

        try {
            const base64 = await blobToBase64(blob);
            onAudioReady(base64, mimeType, durationSec);
        } catch (err: any) {
            setErrorMsg(err?.message || 'Failed to read audio file.');
            setState('error');
        }
    };

    const reset = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setElapsed(0);
        setState('idle');
        setSource(null);
        setPreviewLabel('');
        setErrorMsg(null);
        chunksRef.current = [];
        if (fileInputRef.current) fileInputRef.current.value = '';
        onReset?.();
    };

    if (state === 'recording') {
        return (
            <div className="flex flex-col items-center gap-4 p-6 bg-red-50 border-2 border-red-200 rounded-2xl">
                <div className="flex items-center gap-3">
                    <span className="inline-block w-3 h-3 bg-red-600 rounded-full animate-pulse" />
                    <span className="text-lg font-semibold text-red-700">Recording</span>
                </div>
                <div className="text-3xl font-mono text-red-700 tabular-nums">{formatSeconds(elapsed)}</div>
                <button
                    type="button"
                    onClick={stopRecording}
                    className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-full shadow-md hover:bg-red-700 active:scale-95 transition text-base font-semibold"
                >
                    <Square className="w-5 h-5" /> Stop recording
                </button>
            </div>
        );
    }

    if (state === 'preview' && previewUrl) {
        return (
            <div className="flex flex-col gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-sm text-gray-700 truncate">{previewLabel || (source === 'upload' ? 'Uploaded audio' : 'Recorded audio')}</div>
                    <button
                        type="button"
                        onClick={reset}
                        disabled={isUploading}
                        className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-600 disabled:opacity-50 flex-shrink-0"
                    >
                        <Trash2 className="w-4 h-4" /> {source === 'upload' ? 'Choose different' : 'Re-record'}
                    </button>
                </div>
                <audio src={previewUrl} controls className="w-full" />
                {isUploading && (
                    <div className="flex items-center gap-2 text-sm text-blue-600">
                        <Loader2 className="w-4 h-4 animate-spin" /> Transcribing …
                    </div>
                )}
            </div>
        );
    }

    // Idle / error
    return (
        <div className="flex flex-col items-center gap-3">
            <button
                type="button"
                onClick={startRecording}
                disabled={disabled || state === 'requesting'}
                className="flex items-center gap-3 px-8 py-4 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 active:scale-95 transition text-lg font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
                {state === 'requesting'
                    ? <><Loader2 className="w-6 h-6 animate-spin" /> Requesting mic …</>
                    : <><Mic className="w-6 h-6" /> Start recording</>
                }
            </button>

            <div className="text-xs text-gray-400">or</div>

            <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-full text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
                <Upload className="w-4 h-4" /> Upload existing recording
            </button>
            <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,.m4a,.mp3,.wav,.ogg,.webm,.mp4"
                onChange={e => handleFileChosen(e.target.files?.[0] ?? null)}
                className="hidden"
            />

            <button
                type="button"
                onClick={() => setShowIphoneHint(v => !v)}
                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
            >
                <Info className="w-3 h-3" />
                {showIphoneHint ? 'Hide' : 'Recorded on iPhone Voice Memos?'}
            </button>
            {showIphoneHint && (
                <div className="text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 max-w-md">
                    <ol className="list-decimal list-inside space-y-0.5">
                        <li>In <strong>Voice Memos</strong>: open the recording → tap Share ▲ → <strong>Save to Files</strong></li>
                        <li>Come back here and tap <strong>Upload existing recording</strong> → choose the saved file</li>
                    </ol>
                    <p className="mt-1 italic">Works the same with WhatsApp voice notes, Dictaphone apps, etc.</p>
                </div>
            )}

            {errorMsg && (
                <div className="text-sm text-red-600 max-w-md text-center">{errorMsg}</div>
            )}
        </div>
    );
}
